import React, { useEffect, useRef, useState } from 'react';
import { HelpCircle } from 'lucide-react';

/**
 * Dica de campo, acionada por clique — não por hover.
 *
 * Hover sozinho não funciona em toque e não é alcançável por teclado. O botão abre e fecha, o
 * conteúdo é anunciado por `aria-describedby`, e Escape ou um clique fora fecham.
 *
 * Nasceu na aba Comissão, ao lado do ticket de planejamento, e mudou para cá quando o Dashboard
 * precisou do mesmo recurso: há números em telas diferentes que contam populações diferentes, e a
 * dica é onde esse critério cabe sem virar texto fixo na tela.
 *
 * `alinhamento` existe porque o balão nasce centrado no botão: num card estreito, ou colado à
 * direita da grade, 288px centrados vazam para fora da tela.
 */
const DicaDeCampo = ({ id, titulo, alinhamento = 'centro', children }) => {
  const [aberto, setAberto] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    if (!aberto) return undefined;
    const aoClicarFora = (evento) => { if (containerRef.current && !containerRef.current.contains(evento.target)) setAberto(false); };
    const aoTeclar = (evento) => { if (evento.key === 'Escape') setAberto(false); };
    document.addEventListener('mousedown', aoClicarFora);
    document.addEventListener('keydown', aoTeclar);
    return () => { document.removeEventListener('mousedown', aoClicarFora); document.removeEventListener('keydown', aoTeclar); };
  }, [aberto]);

  const posicao = alinhamento === 'direita'
    ? 'right-0'
    : alinhamento === 'esquerda'
      ? 'left-0'
      : 'left-1/2 -translate-x-1/2';

  return (
    <span className="relative inline-flex" ref={containerRef}>
      <button
        type="button"
        onClick={() => setAberto((v) => !v)}
        aria-label={`O que é ${titulo}`}
        aria-expanded={aberto}
        aria-describedby={aberto ? id : undefined}
        className="grid h-5 w-5 shrink-0 place-items-center rounded-full text-[color:var(--text-muted)] transition hover:bg-[color:var(--surface)] hover:text-[color:var(--accent)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--accent)]"
      >
        <HelpCircle className="h-3.5 w-3.5"/>
      </button>
      {aberto && (
        <span
          id={id}
          role="tooltip"
          className={`absolute top-8 z-30 w-72 max-w-[min(18rem,calc(100vw-2rem))] rounded-lg border border-[color:var(--border)] bg-[color:var(--surface)] p-3 text-xs font-normal leading-5 text-[color:var(--text-muted)] shadow-xl ${posicao}`}
        >
          {children}
        </span>
      )}
    </span>
  );
};

export default DicaDeCampo;
